import { alpha, ListItem, ListItemButton, SxProps, Typography } from '@mui/material';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import React, { memo, useMemo, useState } from 'react';
import Pagination, { PaginationConfig } from '../../supply/searchkit/pagination/Pagination';
import { ScrollableLayout, V } from '../Layout';
import { useStyling } from '../Theme';
import type { EntityName } from '../auth/Security';
import Center from '../components/Center';
import Loading from '../components/Loading';
import I18n from '../i18n/I18n';
import DataListRowActions from './DataList/DataListRowActions';
import type {
    DataListActionHandlers,
    DataListCustomRowAction,
    DataListItem,
    DataListItemRendererComponent
} from './DataList/types';

// DataList is generic to let callsites keep strongly-typed `item` renderers (e.g. `Person`, `Organization`)
// without casts, while preserving the existing runtime contract (selection/keying remains `item.id`-based).
type DataListProps<TItem extends DataListItem = DataListItem> = {
    feature?: string;
    /**
     * Optional only to support callers that still rely on the legacy permission fallback.
     * Make required once those callers forward it and the missing-entity fallback is removed.
     */
    entity?: EntityName;
    id?: string;
    loading?: boolean;
    items: TItem[];
    onClick: (item: TItem) => void;
    refetch?: () => void;
    pagination?: PaginationConfig;
    ItemRenderer: DataListItemRendererComponent<TItem>;
    disableRipple?: boolean;
    divider?: boolean;
    border?: boolean;
    dense?: boolean;
    onEdit?: (id: string, item?: TItem) => void;
    onShare?: (item: TItem) => void;
    onDelete?: (id: string) => void;
    onAttachments?: DataListActionHandlers['onAttachments'];
    onNotes?: DataListActionHandlers['onNotes'];
    showActions?: boolean;
    emptyListElement?: React.ReactNode;
    onActivateOrInactivateHandle?: (row, isInactivate, isActivate) => void;
    listSx?: SxProps;
    listItemSx?: SxProps;
    enableSelectItem?: boolean;
    onCustomAttribute?: (item: TItem) => void;
    onTagHandle?: (item: TItem) => void;
    customRowActions?: DataListCustomRowAction[];
};

const DataListTile = memo(
    ({
        item,
        ItemRenderer,
        onClick,
        disableRipple,
        listItemSx,
        selectedItemId,
        enableSelectItem,
        feature,
        entity,
        showActions,
        actionHandlers,
        customRowActions
    }: {
        item: DataListItem;
        ItemRenderer: DataListItemRendererComponent;
        onClick: (item: DataListItem) => void;
        disableRipple: boolean;
        listItemSx: SxProps;
        selectedItemId: string | null;
        enableSelectItem?: boolean;
        feature?: string;
        entity?: EntityName;
        showActions: boolean;
        actionHandlers: DataListActionHandlers;
        customRowActions: DataListCustomRowAction[];
    }) => {
        const { theme, isDarkMode } = useStyling();
        const [isHover, setIsHover] = useState(false);
        const isSelected = selectedItemId === item.id;
        const isClickableAndSelected = enableSelectItem && isSelected;

        const resolvedListItemSx = typeof listItemSx === 'function' ? listItemSx(theme) : listItemSx;

        return (
            <ListItem disablePadding>
                <ListItemButton
                    onMouseEnter={() => setIsHover(true)}
                    onMouseLeave={() => setIsHover(false)}
                    sx={{
                        pl: 1,
                        pr: 1,
                        position: 'relative',
                        alignItems: 'flex-start',
                        '&:hover': {
                            backgroundColor: isClickableAndSelected
                                ? isDarkMode
                                    ? alpha(theme.palette.primary.main, 0.16)
                                    : alpha(theme.palette.primary.main, 0.08)
                                : theme.palette.grey[isDarkMode ? 700 : 100],
                            cursor: 'pointer'
                        },
                        background: isClickableAndSelected
                            ? isDarkMode
                                ? alpha(theme.palette.primary.main, 0.16)
                                : alpha(theme.palette.primary.main, 0.08)
                            : 'inherit',
                        ...resolvedListItemSx
                    }}
                    onClick={() => onClick(item)}
                    disableRipple={disableRipple}
                >
                    <ItemRenderer item={item} enableSelectItem={enableSelectItem} isItemSelected={isSelected} />
                    {showActions && feature && (
                        <DataListRowActions
                            item={item}
                            feature={feature}
                            entity={entity}
                            actionHandlers={actionHandlers}
                            customRowActions={customRowActions}
                            isActivated={isHover || isSelected}
                        />
                    )}
                </ListItemButton>
            </ListItem>
        );
    },

    (prev, next) =>
        prev.item === next.item &&
        prev.selectedItemId === next.selectedItemId &&
        prev.enableSelectItem === next.enableSelectItem &&
        prev.listItemSx === next.listItemSx &&
        prev.feature === next.feature &&
        prev.entity === next.entity &&
        prev.showActions === next.showActions &&
        prev.actionHandlers === next.actionHandlers &&
        prev.customRowActions === next.customRowActions
);

export default function DataList<TItem extends DataListItem = DataListItem>(props: DataListProps<TItem>) {
    const {
        id,
        loading = false,
        items,
        refetch,
        pagination,
        onClick = undefined,
        ItemRenderer,
        onActivateOrInactivateHandle = undefined,
        disableRipple = false,
        divider = false,
        border = false,
        dense = false,
        feature,
        entity,
        onEdit,
        onShare,
        onDelete,
        onAttachments,
        onNotes,
        showActions = true,
        emptyListElement = undefined,
        listSx = {},
        listItemSx = {},
        enableSelectItem,
        onCustomAttribute = undefined,
        onTagHandle = undefined,
        customRowActions = []
    } = props;
    const { theme } = useStyling();
    const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

    const actionHandlers = useMemo<DataListActionHandlers>(
        () => ({
            onEdit,
            onShare,
            onDelete: onDelete ? (itemId: string) => onDelete(itemId) : undefined,
            onAttachments,
            onNotes,
            onActivateOrInactivateHandle,
            onCustomAttribute,
            onTagHandle
        }),
        [
            onEdit,
            onShare,
            onDelete,
            onAttachments,
            onNotes,
            onActivateOrInactivateHandle,
            onCustomAttribute,
            onTagHandle
        ]
    );

    const handleTileClick = (item: TItem) => {
        if (enableSelectItem) {
            setSelectedItemId((prev) => (prev === item.id ? null : item.id));
        }
        onClick?.(item);
    };

    return (
        <V
            sx={{
                backgroundColor: theme.palette.background.paper,
                height: '100%',
                overflow: 'hidden',
                border: border ? `1px solid ${theme.palette.divider}` : 'none',
                borderRadius: 1
            }}
        >
            <ScrollableLayout sx={{ pt: 0, pb: 0, pr: 1 }}>
                <List
                    id={`${id}-list`}
                    sx={{ height: '100%', width: '100%', overflowY: 'auto', ...listSx }}
                    dense={dense}
                >
                    {loading ? (
                        <Loading />
                    ) : items && items.length ? (
                        items.map((item, index) => {
                            return (
                                <React.Fragment key={item.id}>
                                    <DataListTile
                                        item={item}
                                        ItemRenderer={ItemRenderer}
                                        onClick={handleTileClick}
                                        disableRipple={disableRipple}
                                        listItemSx={listItemSx}
                                        selectedItemId={selectedItemId}
                                        enableSelectItem={enableSelectItem}
                                        feature={feature}
                                        entity={entity}
                                        showActions={showActions}
                                        actionHandlers={actionHandlers}
                                        customRowActions={customRowActions}
                                    />
                                    {divider && index < items.length - 1 && <Divider />}
                                </React.Fragment>
                            );
                        })
                    ) : (
                        <Center>
                            {emptyListElement || (
                                <Typography>
                                    <I18n token={'error.listEmpty'} />
                                </Typography>
                            )}
                        </Center>
                    )}
                </List>
            </ScrollableLayout>
            {pagination && <Pagination id={id} pagination={pagination} />}
        </V>
    );
}
